import { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
	Thead,
  Tbody,
	Tfoot,
  Td,
  Text,
  Th,
  Tr,
	Checkbox,
	Image
} from '@chakra-ui/react';
import { FiCheckCircle, FiMoreVertical } from 'react-icons/fi';
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';

import { useAuth } from '../../../hooks/auth';
import { formatPrice } from '../../../utils/format';
import { FinJsonDTO } from '../../../dtos/FinJsonDTO';
import ImpressaoLancamento from '../../ComponentsLancamento/ImpressaoLancamento';

type TextAlertData = {
	header: string;
	body: string;
	is_toast: boolean;
}
type SortData = {
  por: string;
  cres: boolean;
};
type ControlsData = {
	page: number;
	perPage: number;
	totalPage: number;
};
type UpdateListData = {
	page: number;
	perPage: number;
	lancs: LancamentoData[];
};
type LancamentoData = {
	caixa: string;
	compensado: boolean;
	data: string;
	doc: string;
	fin_json: FinJsonDTO;
	id_lancamento: string;
	name_pay: string;
	nome: string;
	operacao: string;
	operacao_tipo: string;
	url_pay: string;
	vlr_cred: number;
	vlr_cred_formatted?: string;
	vlr_deb: number;
	vlr_deb_formatted?: string;
};

interface DTTableParams {
  lancamentos: LancamentoData[];
  pesqLancamentos: LancamentoData[];
  sort: SortData;
  setSort: (sort: SortData) => void;
  controls: ControlsData;
	handleUpdateList: (params: UpdateListData) => void;
	setLancamentoModal: (lancamento: FinJsonDTO) => void;
	setModalLancamentoVisible: (open: boolean) => void;
	setModalLancamentoEditVisible: (open: boolean) => void;
	setLancamentoDelete: (lanc: FinJsonDTO) => void;
	setOpenAlert: (open: boolean) => void;
	setTextAlert: (texts: TextAlertData) => void;
	// setModalTituloDeleteVisible: (open: boolean) => void;
};

const DTTable: React.FC<DTTableParams> = ({
	lancamentos,
	pesqLancamentos,
  sort,
  setSort,
  controls,
  handleUpdateList,
	setLancamentoModal,
	setModalLancamentoVisible,
	setModalLancamentoEditVisible,
	setLancamentoDelete,
	setOpenAlert,
	setTextAlert,
}) => {
	const { permissions } = useAuth();

	const [vCredTotal, setVCredTotal] = useState('');
	const [vDebTotal, setVDebTotal] = useState('');
	
  function handleSort(prop: string) {
		let mapped: LancamentoData[] = [];

    const auxCres = !sort.cres;

    if (sort.por === prop) {
      if (auxCres) {
        mapped = pesqLancamentos.sort((a, b) => {
          return (
            +(Object(a)[prop] > Object(b)[prop]) ||
            +(Object(a)[prop] === Object(b)[prop]) - 1
          );
        });
      } else {
        mapped = pesqLancamentos.sort((a, b) => {
          return (
            +(Object(a)[prop] < Object(b)[prop]) ||
            +(Object(a)[prop] === Object(b)[prop]) - 1
          );
        });
      }
      setSort({ ...sort, cres: auxCres });
    } else {
      mapped = pesqLancamentos.sort((a, b) => {
        return (
          +(Object(a)[prop] > Object(b)[prop]) ||
          +(Object(a)[prop] === Object(b)[prop]) - 1
        );
      });
      setSort({ por: prop, cres: true });
    }

    // setSort({ por: prop, cres: !sort.cres });

    handleUpdateList({ page: 1, perPage: controls.perPage, lancs: mapped });
  };

	useEffect(() => {
		let auxVCred = 0;
		let auxVDeb = 0;

		for (let i = 0; i < lancamentos.length; i++) {
			auxVCred = auxVCred + lancamentos[i].vlr_cred;
			auxVDeb = auxVDeb + lancamentos[i].vlr_deb;
		};

		setVCredTotal(formatPrice(auxVCred));
		setVDebTotal(formatPrice(auxVDeb));
	}, [lancamentos]);

  return (
    <Table colorScheme="blackAlpha" size="sm" whiteSpace="nowrap">
      <Thead>
        <Tr>
					<Th cursor="pointer" onClick={() => handleSort('nome')}>
            <Flex align="center">
              Nome
							{sort.por === 'nome' && sort.cres ? (
                <Icon as={MdArrowDropUp} fontSize="20" />
              ) : (
									sort.por === 'nome' &&
                !sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
              )}
            </Flex>
          </Th>

					<Th cursor="pointer" onClick={() => handleSort('doc')}>
						<Flex align="center">
							N. Doc.
							{sort.por === 'doc' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
								sort.por === 'doc' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th>

					<Th cursor="pointer" onClick={() => handleSort('name_pay')}>
						<Flex justifyContent="flex-start">
              Pagto.
              {sort.por === 'name_pay' && sort.cres ? (
                <Icon as={MdArrowDropUp} fontSize="20" />
              ) : (
                sort.por === 'name_pay' &&
                !sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
              )}
            </Flex>
          </Th>

					<Th cursor="pointer" onClick={() => handleSort('caixa')}>
						<Flex>
							Conta
							{sort.por === 'caixa' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
								sort.por === 'caixa' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th>

					<Th cursor="pointer" onClick={() => handleSort('vlr_cred')}>
						<Flex justifyContent="flex-end">
							Vlr Cred.
							{sort.por === 'vlr_cred' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
								sort.por === 'vlr_cred' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th>

					<Th cursor="pointer" onClick={() => handleSort('vlr_deb')}>
						<Flex justifyContent="flex-end">
							Vlr Deb.
							{sort.por === 'vlr_deb' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
								sort.por === 'vlr_deb' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th>

					<Th cursor="pointer" onClick={() => handleSort('compensado')}>
						<Flex justifyContent="center">
							Comp.
							{sort.por === 'compensado' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
									sort.por === 'compensado' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th>

					<Th cursor="pointer" onClick={() => handleSort('data')}>
						<Flex justifyContent="flex-end" px="0">
							Data
							{sort.por === 'data' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
								sort.por === 'data' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th>

					<Th cursor="pointer" onClick={() => handleSort('operacao')}>
						<Flex justifyContent="flex-end">
							Op.
							{sort.por === 'operacao' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
									sort.por === 'operacao' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th> 

					<Th cursor="pointer" onClick={() => handleSort('operacao_tipo')}>
						<Flex justifyContent="flex-end">
							Tipo Op.
							{sort.por === 'operacao_tipo' && sort.cres ? (
								<Icon as={MdArrowDropUp} fontSize="20" />
							) : (
								sort.por === 'operacao_tipo' &&
								!sort.cres && <Icon as={MdArrowDropDown} fontSize="20" />
							)}
						</Flex>
					</Th>

					<Th></Th>

					{(permissions.incluir || permissions.alterar) && (
						<Th></Th>
					)}
        </Tr>
      </Thead>

      <Tbody>
        {lancamentos.map(lancamento => {
          return (
						<Tr
							key={lancamento.id_lancamento}
							fontSize={14}
							fontWeight={500}
							cursor="pointer"
						>
							{/* NOME */}
              <Td py={2} cursor="pointer">
                <Box>
                  <Text color="blue.500">
										{lancamento.nome === "" ? "--" : lancamento.nome}
                  </Text>
                </Box>
              </Td>

							{/* DOC */}
							<Td py={2} cursor="pointer">
								<Box>
									<Text color="gray.500">{lancamento.doc}</Text>
								</Box>
							</Td>

							{/* NAME PAY */}
							<Td isNumeric py={2} color="gray.500">
								<Flex align="center" justify="flex-start">
									{lancamento.url_pay && (
										<Image src={lancamento.url_pay} objectFit='cover' w='25px' h="20px" mr={2} />
									)}
									{lancamento.name_pay}
								</Flex>
              </Td>

							{/* CAIXA */}
							<Td py={2} color="gray.500">
								{lancamento.caixa}
							</Td>

							{/* VLR CRED */}
							<Td isNumeric py={2} color="gray.500">
								{lancamento.vlr_cred_formatted}
							</Td>

							{/* VLR DEB */}
							<Td isNumeric py={2} color="gray.500">
								{lancamento.vlr_deb_formatted}
							</Td>

							{/* COMPENSADO */}
							<Td py={2} color="gray.500">
								<Flex justifyContent="center">
									{lancamento.compensado
										? <Icon as={FiCheckCircle} fontSize={20} color="blue.500" />
										: '--'
									}
								</Flex>
							</Td>

							{/* DATA */}
							<Td isNumeric py={2} color="gray.500">
								{lancamento.data}
							</Td>

							{/* OPERAÇÃO */}
							<Td isNumeric py={2} color="gray.500">
								{lancamento.operacao}
							</Td>

							{/* OPERAÇÃO TIPO */}
							<Td isNumeric py={2} color="gray.500">
								{lancamento.operacao_tipo}
							</Td>

							{/* IMPRIMIR */}
							<Td px={0}>
								<ImpressaoLancamento lancamento={lancamento.fin_json} />
							</Td>

							{/* MENU */}
							{(permissions.alterar || permissions.alterar) && (
								<Td px={0}>
									<Menu>
										{({ isOpen }) => (
											<>
												<MenuButton
													border={0}
													fontSize="1.5626rem"
													as={IconButton}
													isActive={isOpen}
													aria-label="Options"
													icon={<FiMoreVertical />}
													variant="outline"
													_hover={{ bg: 'gray.50' }}
												/>

												<MenuList p={0} boxShadow='xl'>
													{/* DETALHES */}
													<MenuItem
														fontSize={16}
														fontWeight={500}
														py={3}
														_hover={{ bg: 'gray.50' }}
														onClick={() => {
															setLancamentoModal(lancamento.fin_json);
															setModalLancamentoVisible(true);
														}}
													>
														Detalhes
													</MenuItem>

													{/* EDITAR */}
													{permissions.alterar && (
														<>
															<Divider />

															<MenuItem
																fontSize={16}
																fontWeight={500}
																py={3}
																_hover={{ bg: 'gray.50' }}
																onClick={() => {
																	setLancamentoModal(lancamento.fin_json);
																	setModalLancamentoEditVisible(true);
																}}
															>
																Editar
															</MenuItem>
														</>
													)}

													{/* EXCLUIR */}
													{permissions.excluir && (
														<>
															<Divider />

															<MenuItem
																fontSize={16}
																fontWeight={500}
																py={3}
																_hover={{ bg: 'gray.50' }}
																/* isDisabled={
																	(lancamento.fin_json.baixas && lancamento.fin_json.baixas.length > 0) ||
																	(lancamento.fin_json.documentos && lancamento.fin_json.documentos.length > 0)
																} */
																onClick={() => {
																	setLancamentoDelete(lancamento.fin_json);
																	setTextAlert({
																		header: `Excluir Lançamento!`,
																		body: 'Você está excluindo este lançamento!' + "\n" + 'Deseja mesmo excluir?',
																		is_toast: true,
																	});
																	setOpenAlert(true);
																}}
															>
																Excluir
															</MenuItem>
														</>
													)}
												</MenuList>
											</>
										)}
									</Menu>
								</Td>
							)}
            </Tr>
          );
        })}
      </Tbody>

			<Tfoot>
				<Tr>
					<Th></Th>
					<Th pt={4}>Totais</Th>
					<Th></Th>
					<Th></Th>
					<Th></Th>
					<Th isNumeric>{vCredTotal}</Th>
					<Th isNumeric>{vDebTotal}</Th>
					<Th></Th>
					<Th></Th>
					<Th></Th>
					<Th></Th>
					<Th></Th>
				</Tr>
			</Tfoot>
    </Table>
  );
};

export default DTTable;
